// ─── Little celebrations ──────────────────────────────────────────────────────────
// Brand-coloured particle bursts, card flashes/shakes and a heart pop for the moments
// that matter: a task submitted or approved, an invoice paid, leave approved, a client
// approving a post, clocking in. Pure DOM + the Web Animations API — no library, nothing
// left behind (every node removes itself), and nothing at all for anyone with "reduce
// motion" switched on.
//
//   celebrate('approve')            → burst at the last place the user clicked/tapped
//   celebrate('submit', element)    → burst from the middle of an element
//   pulse(element, 'flash'|'shake'|'pop')
//   heartPop(element)

const BRAND = ['#A98953', '#C7A56A', '#BFA067', '#EFEBE4', '#E2C044'];
const GREEN = ['#8FB08A', '#A9C7A4', '#EFEBE4', '#C7A56A'];
const PRESETS = {
  submit:  { count: 18, spread: 70,  colors: BRAND, size: [4, 7],  gravity: 0.6, shapes: ['dot', 'spark'] },
  approve: { count: 42, spread: 150, colors: BRAND, size: [5, 9],  gravity: 1,   shapes: ['dot', 'spark', 'square'] },
  paid:    { count: 26, spread: 110, colors: ['#C7A56A', '#A98953', '#E2C044', '#EFEBE4'], size: [7, 11], gravity: 1.2, shapes: ['coin'] },
  clockin: { count: 30, spread: 120, colors: GREEN, size: [4, 8],  gravity: 0.7, shapes: ['dot', 'spark'] },
  clockout:{ count: 16, spread: 70,  colors: BRAND, size: [3, 6],  gravity: 0.4, shapes: ['spark'] },
  small:   { count: 8,  spread: 34,  colors: BRAND, size: [3, 5],  gravity: 0.4, shapes: ['dot'] },
  leave:   { count: 28, spread: 110, colors: GREEN, size: [4, 8],  gravity: 0.9, shapes: ['dot', 'square'] },
};

export const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const canAnimate = () => typeof document !== 'undefined' && typeof Element !== 'undefined' && !!Element.prototype.animate && !reducedMotion();

// Remember where the user last clicked/tapped, so an action can celebrate right there
// even when it finishes after a network round-trip.
let lastPoint = null;
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', e => { lastPoint = { x: e.clientX, y: e.clientY, t: Date.now() }; }, { capture: true, passive: true });
}
function originOf(el) {
  if (el && el.getBoundingClientRect) {
    const r = el.getBoundingClientRect();
    if (r.width || r.height) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  if (lastPoint && Date.now() - lastPoint.t < 8000) return lastPoint;
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}
const rand = (a, b) => a + Math.random() * (b - a);

function particle(shape, color, size) {
  const p = document.createElement('div');
  const s = p.style;
  s.position = 'fixed'; s.left = '0'; s.top = '0'; s.pointerEvents = 'none'; s.zIndex = '4000';
  s.width = size + 'px'; s.height = size + 'px'; s.background = color; s.willChange = 'transform, opacity';
  if (shape === 'dot') s.borderRadius = '50%';
  if (shape === 'spark') { s.width = Math.max(2, size / 3) + 'px'; s.height = size * 2 + 'px'; s.borderRadius = '2px'; }
  if (shape === 'square') s.borderRadius = '1px';
  if (shape === 'coin') { s.borderRadius = '50%'; s.background = `radial-gradient(circle at 35% 30%, #F3E3B5, ${color} 55%, #7A6235)`; s.boxShadow = '0 0 6px rgba(199,165,106,.55)'; }
  return p;
}

export function celebrate(kind = 'approve', el) {
  if (!canAnimate()) return;
  const cfg = PRESETS[kind] || PRESETS.approve;
  const { x, y } = originOf(el);
  for (let i = 0; i < cfg.count; i++) {
    const shape = cfg.shapes[i % cfg.shapes.length];
    const p = particle(shape, cfg.colors[i % cfg.colors.length], rand(cfg.size[0], cfg.size[1]));
    document.body.appendChild(p);
    const angle = rand(-Math.PI, 0) + rand(-0.35, 0.35);           // mostly upward fan
    const dist = rand(cfg.spread * 0.45, cfg.spread);
    const dx = Math.cos(angle) * dist, dy = Math.sin(angle) * dist;
    const fall = cfg.gravity * rand(60, 120);
    const spin = rand(-540, 540);
    const dur = rand(650, 1050);
    const anim = p.animate([
      { transform: `translate(${x}px, ${y}px) rotate(0deg) scale(1)`, opacity: 1 },
      { transform: `translate(${x + dx * 0.75}px, ${y + dy * 0.75}px) rotate(${spin * 0.6}deg) scale(1)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${x + dx}px, ${y + dy + fall}px) rotate(${spin}deg) scale(.6)`, opacity: 0 },
    ], { duration: dur, easing: 'cubic-bezier(.15,.75,.3,1)', fill: 'forwards' });
    anim.onfinish = () => p.remove();
    setTimeout(() => p.remove(), dur + 200);                         // belt and braces
  }
  if (kind === 'approve' || kind === 'clockin' || kind === 'paid') ring(x, y, kind === 'clockin' ? '#8FB08A' : '#C7A56A');
}

// An expanding ring — the "something happened here" ripple.
export function ring(x, y, color = '#C7A56A') {
  if (!canAnimate()) return;
  const r = document.createElement('div');
  Object.assign(r.style, { position: 'fixed', left: '0', top: '0', width: '14px', height: '14px', borderRadius: '50%', border: `2px solid ${color}`,
    pointerEvents: 'none', zIndex: '3999', willChange: 'transform, opacity' });
  document.body.appendChild(r);
  const a = r.animate([
    { transform: `translate(${x - 7}px, ${y - 7}px) scale(1)`, opacity: 0.9 },
    { transform: `translate(${x - 7}px, ${y - 7}px) scale(9)`, opacity: 0 },
  ], { duration: 700, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
  a.onfinish = () => r.remove();
  setTimeout(() => r.remove(), 900);
}

// Flash / shake / pop an existing element (a task card, a row, a button).
const PULSES = {
  flash: [{ boxShadow: '0 0 0 0 rgba(169,137,83,0)' }, { boxShadow: '0 0 0 2px rgba(199,165,106,.85), 0 0 34px rgba(169,137,83,.45)', offset: 0.3 }, { boxShadow: '0 0 0 0 rgba(169,137,83,0)' }],
  green: [{ boxShadow: '0 0 0 0 rgba(143,176,138,0)' }, { boxShadow: '0 0 0 2px rgba(143,176,138,.85), 0 0 30px rgba(143,176,138,.4)', offset: 0.3 }, { boxShadow: '0 0 0 0 rgba(143,176,138,0)' }],
  shake: [{ transform: 'translateX(0)' }, { transform: 'translateX(-7px)' }, { transform: 'translateX(6px)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(3px)' }, { transform: 'translateX(0)' }],
  pop:   [{ transform: 'scale(1)' }, { transform: 'scale(1.035)', offset: 0.35 }, { transform: 'scale(.995)', offset: 0.7 }, { transform: 'scale(1)' }],
  tick:  [{ transform: 'scale(1)' }, { transform: 'scale(1.35)', offset: 0.4 }, { transform: 'scale(1)' }],
  wiggle:[{ transform: 'rotate(0)' }, { transform: 'rotate(-14deg)' }, { transform: 'rotate(12deg)' }, { transform: 'rotate(-8deg)' }, { transform: 'rotate(5deg)' }, { transform: 'rotate(0)' }],
};
const PULSE_MS = { flash: 1000, green: 1000, shake: 450, pop: 420, tick: 300, wiggle: 650 };
export function pulse(el, kind = 'flash') {
  if (!el || !canAnimate() || !PULSES[kind]) return;
  el.animate(PULSES[kind], { duration: PULSE_MS[kind], easing: 'cubic-bezier(.2,.8,.2,1)' });
}
// Find a task card by id and pulse it (task cards carry data-task-id).
export function pulseTask(taskId, kind) {
  if (typeof document === 'undefined') return;
  pulse(document.querySelector(`[data-task-id="${String(taskId).replace(/"/g, '')}"]`), kind);
}

// Instagram-style heart over a post — for a client approving it.
export function heartPop(el) {
  if (!canAnimate()) return;
  const { x, y } = originOf(el);
  const h = document.createElement('div');
  h.innerHTML = '<svg viewBox="0 0 24 24" width="96" height="96"><path fill="#EFEBE4" d="M12 21s-7.5-4.6-10-9.4C.4 8.3 2.3 4.5 6 4.1c2-.2 3.6.8 4.6 2.2.4.5.8 1.1 1.4 1.1s1-.6 1.4-1.1C14.4 4.9 16 3.9 18 4.1c3.7.4 5.6 4.2 4 7.5C19.5 16.4 12 21 12 21z"/></svg>';
  Object.assign(h.style, { position: 'fixed', left: '0', top: '0', pointerEvents: 'none', zIndex: '4001', filter: 'drop-shadow(0 6px 18px rgba(0,0,0,.45))' });
  document.body.appendChild(h);
  const a = h.animate([
    { transform: `translate(${x - 48}px, ${y - 48}px) scale(0)`, opacity: 0 },
    { transform: `translate(${x - 48}px, ${y - 48}px) scale(1.18)`, opacity: 1, offset: 0.25 },
    { transform: `translate(${x - 48}px, ${y - 48}px) scale(.95)`, opacity: 1, offset: 0.4 },
    { transform: `translate(${x - 48}px, ${y - 48}px) scale(1)`, opacity: 1, offset: 0.75 },
    { transform: `translate(${x - 48}px, ${y - 70}px) scale(.8)`, opacity: 0 },
  ], { duration: 1100, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
  a.onfinish = () => h.remove();
  setTimeout(() => h.remove(), 1300);
  celebrate('small', el);
}

// A small ripple where a button was tapped — the "it heard you" feedback on every press.
export function tapRipple(x, y, color = 'rgba(199,165,106,.75)') {
  if (!canAnimate()) return;
  const r = document.createElement('div');
  Object.assign(r.style, { position: 'fixed', left: '0', top: '0', width: '10px', height: '10px', borderRadius: '50%',
    border: `1.5px solid ${color}`, pointerEvents: 'none', zIndex: '3998', willChange: 'transform, opacity' });
  document.body.appendChild(r);
  const a = r.animate([
    { transform: `translate(${x - 5}px, ${y - 5}px) scale(.6)`, opacity: 0.9 },
    { transform: `translate(${x - 5}px, ${y - 5}px) scale(4.2)`, opacity: 0 },
  ], { duration: 460, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
  a.onfinish = () => r.remove();
  setTimeout(() => r.remove(), 650);
}
